// Jump from a blocker chip to the field it names (DEV-2995).
//
// MUI's [name="..."] anchor is a HIDDEN input for both field shapes the appointment
// form uses, and SelectFormField's kebab-cased id belongs to its InputLabel, not its
// control — so neither is a usable scroll or focus target. What IS reliable is the
// .MuiFormControl-root wrapping both. Evidence:
// .suno-flow/features/dev-2995/proof/anchor-resolution.md
const VISIBLE_CONTROL_SELECTOR = [
  ".MuiSelect-select",
  '[role="combobox"]',
  'input:not([type="hidden"])',
  "textarea"
].join(", ");

const findAnchor = (name: string, root: ParentNode) =>
  root.querySelector<HTMLElement>(`[name="${name}"]`);

/** Whether a blocker can be jumped to — lets callers render a non-jumpable one as
 *  plain text instead of a button that would do nothing. */
export const canFocusFormField = (
  name: string,
  root: ParentNode = document
): boolean => Boolean(findAnchor(name, root));

/**
 * Scroll a form field into view and focus it. Returns false if it isn't rendered. `root` limits
 * the search, for a dialog whose field names could also appear on the page behind it.
 */
export default function focusFormField(
  name: string,
  root: ParentNode = document
): boolean {
  const anchor = findAnchor(name, root);

  if (!anchor) {
    return false;
  }

  const container =
    anchor.closest<HTMLElement>(".MuiFormControl-root") ?? anchor;
  // Optional call on purpose: this runs inside Save's click handler, and a missing
  // scrollIntoView must never take submit down with it.
  container.scrollIntoView?.({ block: "center" });

  const control =
    container.querySelector<HTMLElement>(VISIBLE_CONTROL_SELECTOR) ??
    (container === anchor ? anchor : null);
  control?.focus();

  return true;
}
